import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { type UTCDate } from '@date-fns/utc';
import Card from '@/shared/components/flip-ui-kit/Card';
import { getCachePoolsQuery } from '@/shared/graphql/cache/pools';
import { type ChainflipAsset } from '@/shared/graphql/generated/cache/graphql';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import ChartIcon from '@/shared/icons/flip-ui-kit/large/Chart';
import DropletIcon from '@/shared/icons/flip-ui-kit/large/Droplet';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/large/MoneyBag';
import TargetIcon from '@/shared/icons/flip-ui-kit/large/Target';
import SlantedArrowIcon from '@/shared/icons/flip-ui-kit/small/SlantedArrow';
import { getTimeframeOrFormattedDateLabels } from '@/shared/utils/date';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import { type RpcOrder } from '@/shared/utils/orders';
import TokenAmount from '@/shared/utils/TokenAmount';
import { getCacheLiquidityQuery } from '../../graphql/cache/liquidity';
import { getPoolAggregatesByDateQuery } from '../../graphql/explorer/pools';
import { getTotalBoostFeesForPool } from '../../graphql/lp/boost';

export const PoolCards = ({
  startDate,
  endDate,
  baseAsset,
  openOrders,
  openOrdersLoading,
}: {
  startDate: UTCDate;
  endDate: UTCDate;
  baseAsset?: BaseChainflipAsset;
  openOrders?: (RpcOrder & { createdAt?: string; valueUsd: string })[];
  openOrdersLoading?: boolean;
}) => {
  const { prices: assetPrices } = useChainflipAssetPrices();
  const { data: aggregatesData, isLoading: aggregatesLoading } = useGqlQuery(
    getPoolAggregatesByDateQuery,
    {
      variables: {
        startingTimestamp: startDate.toISOString(),
      },
      refetchInterval: 60_000,
    },
  );
  const { data: liquidityData, isLoading: liquidityDataLoading } = useGqlQuery(
    getCacheLiquidityQuery,
    {
      context: { clientName: 'statechainCache' },
      refetchInterval: 60_000,
    },
  );
  const { data: poolData, isLoading: poolDataLoading } = useGqlQuery(getCachePoolsQuery, {
    context: { clientName: 'statechainCache' },
    refetchInterval: 60_000,
  });

  const { data: boostFeeData, isLoading: boostFeeDataLoading } = useGqlQuery(
    getTotalBoostFeesForPool,
    {
      context: { clientName: 'lpProcessor' },
      refetchInterval: 60_000,
      enabled: baseAsset === undefined,
      variables: {
        startingTimestamp: startDate.toISOString(),
      },
    },
  );

  const aggregatedSellOrders = openOrders?.reduce((acc, curr) => {
    const order = curr.type === 'ask' ? Number(curr.sell_amount) : 0;
    return acc + order;
  }, 0);

  const aggregatedBuyOrders = openOrders?.reduce((acc, curr) => {
    const order = curr.type === 'bid' ? Number(curr.sell_amount) : 0;
    return acc + order;
  }, 0);

  const swapAggregates = aggregatesData?.poolSwaps?.groupedAggregates ?? [];
  const poolLiquiditySnapshots = poolData?.pools?.nodes ?? [];
  const undeployedLiquidityAggregates = liquidityData?.balances?.groupedAggregates ?? [];

  const undeployedLiquidityUsd = undeployedLiquidityAggregates.reduce((acc, curr) => {
    const asset = curr.keys?.[0] as ChainflipAsset;
    const usdValue =
      TokenAmount.fromAsset(curr.sum?.amount, asset)
        ?.mul(assetPrices[asset] ?? 0)
        .toNumber() ?? 0;
    return acc + usdValue;
  }, 0);

  let deployedLiquidityUsd = 0;
  for (const pool of poolLiquiditySnapshots) {
    if (!baseAsset || baseAsset === pool.baseAsset) {
      deployedLiquidityUsd +=
        TokenAmount.fromAsset(pool.baseLiquidityAmount, pool.baseAsset)
          ?.mul(assetPrices[pool.baseAsset] ?? 0)
          .toNumber() ?? 0;

      deployedLiquidityUsd +=
        TokenAmount.fromAsset(pool.quoteLiquidityAmount, pool.quoteAsset)
          ?.mul(assetPrices[pool.quoteAsset] ?? 0)
          .toNumber() ?? 0;
    }
  }

  let volumeUsd = 0;
  for (const aggregate of swapAggregates) {
    if (!baseAsset || aggregate.fromAssetToAsset?.includes(baseAsset)) {
      volumeUsd += Number(aggregate.sum?.toValueUsd ?? 0);
    }
  }

  const boostDeployed = Object.values(liquidityData?.boost?.aggregates?.sum ?? {}).reduce(
    (acc, amount) => acc + Number(amount) || 0,
    0,
  );

  const strategiesLiquidityUsd = Object.values(
    liquidityData?.strategies?.aggregates?.sum ?? {},
  ).reduce((acc, amount) => acc + Number(amount) || 0, 0);

  return (
    <>
      <Card
        loading={poolDataLoading}
        icon={<DropletIcon className="space-x-8 text-cf-blue-2" />}
        title="Deployed Liquidity"
        tooltip={
          baseAsset
            ? 'The combined USD value of open positions held by liquidity providers in this pool.'
            : 'The combined USD value of open positions held by liquidity providers across all pools.'
        }
        footer="Current"
      >
        <div className="flex h-full items-center text-16">
          {formatUsdValue(deployedLiquidityUsd)}
        </div>
      </Card>
      {openOrders && baseAsset && (
        <Card
          loading={openOrdersLoading}
          icon={<SlantedArrowIcon className="text-cf-green-3" width={24} height={24} />}
          title="Buy Orders"
          tooltip={`Total ${chainflipAssetMap.Usdc.symbol} liquidity currently available to buy ${chainflipAssetMap[baseAsset].symbol} in this pool`}
          footer="Current"
        >
          <div className="flex h-full items-center gap-x-1">
            <span className="font-aeonikMedium text-16 text-cf-white">
              {TokenAmount.fromAsset(aggregatedBuyOrders ?? 0, 'Usdc')?.toCapped()}{' '}
              {chainflipAssetMap.Usdc.symbol}
            </span>
            <span className="font-aeonikRegular text-12 text-cf-light-2">
              {formatUsdValue(
                TokenAmount.fromAsset(aggregatedBuyOrders ?? 0, 'Usdc')
                  ?.mul(assetPrices.Usdc ?? 0)
                  .toFixed(2),
              )}
            </span>
          </div>
        </Card>
      )}
      {openOrders && baseAsset && (
        <Card
          loading={openOrdersLoading}
          icon={<SlantedArrowIcon className="rotate-180 text-cf-red-2" width={24} height={24} />}
          title="Sell Orders"
          tooltip={`Total ${chainflipAssetMap[baseAsset].symbol} liquidity currently available to sell into ${chainflipAssetMap.Usdc.symbol} in this pool`}
          footer="Current"
        >
          <div className="flex h-full items-center gap-x-1">
            <span className="font-aeonikMedium text-16 text-cf-white">
              {TokenAmount.fromAsset(aggregatedSellOrders ?? 0, baseAsset)?.toCapped()}{' '}
              {chainflipAssetMap[baseAsset].symbol}
            </span>
            <span className="font-aeonikRegular text-12 text-cf-light-2">
              {formatUsdValue(
                TokenAmount.fromAsset(aggregatedSellOrders ?? 0, baseAsset)
                  ?.mul(assetPrices[baseAsset] ?? 0)
                  .toFixed(2),
              )}
            </span>
          </div>
        </Card>
      )}
      {!baseAsset && (
        <Card
          loading={liquidityDataLoading}
          icon={<DropletIcon className="text-cf-red-2" />}
          title="Undeployed Liquidity"
          tooltip={
            baseAsset
              ? 'The combined balance in USD value held by liquidity providers in this pool.'
              : 'The cumulative USD value of assets held by liquidity provider accounts that can be deployed to a pool instantly'
          }
          footer="Current"
        >
          <div className="flex h-full items-center text-16">
            {formatUsdValue(undeployedLiquidityUsd)}
          </div>
        </Card>
      )}
      {!baseAsset && (
        <Card
          loading={liquidityDataLoading}
          icon={<TargetIcon className="space-x-8 text-cf-white" />}
          title="Strategies Liquidity"
          tooltip="The combined value (in USD) of all active stablecoin strategies."
          footer="Current"
        >
          <div className="text-16">{formatUsdValue(strategiesLiquidityUsd)}</div>
        </Card>
      )}
      {!baseAsset && (
        <Card
          loading={liquidityDataLoading}
          icon={<DropletIcon className="text-cf-pink-1" />}
          title="Boost Liquidity"
          tooltip="The combined USD value of all boost liquidity, across all boost pools"
          footer="Current"
        >
          <div className="flex h-full items-center text-16">{formatUsdValue(boostDeployed)}</div>
        </Card>
      )}
      <Card
        loading={aggregatesLoading}
        icon={<ChartIcon className="text-cf-orange-2" />}
        title="Volume"
        tooltip={
          baseAsset
            ? 'The cumulative USD value of assets traded in this pool, over this period.'
            : 'The cumulative USD value of assets traded across all pools, over this period.'
        }
        footer={getTimeframeOrFormattedDateLabels([startDate, endDate])}
      >
        <div className="flex h-full items-center text-16">{formatUsdValue(volumeUsd)}</div>
      </Card>
      {!baseAsset && (
        <Card
          loading={boostFeeDataLoading}
          icon={<MoneyBagIcon className="text-cf-blue-4" />}
          title="Earned Boost Fees"
          tooltip="The cumulative USD value of earned fees across all boost pools over this period"
          footer={getTimeframeOrFormattedDateLabels([startDate, endDate])}
        >
          <div className="flex h-full items-center text-16">
            {formatUsdValue(boostFeeData?.allBoosts?.aggregates?.sum?.feeUsd ?? 0)}
          </div>
        </Card>
      )}
    </>
  );
};
